import * as yup from 'yup';

import { AttestationsListQuery, VerifierType, VerifierTypeOfGovernment } from 'src/api';
import { AddressFieldsFormFields } from 'src/constants/address';
import {
  addressFieldsValidationSchema,
  ERROR_MESSAGE_REQUIRED_ZIP_CODE,
  emailValidationSchema,
  numberOfEmployeesSimpleValidationSchema,
  passwordValidationSchema,
  phoneNumberValidationSchema,
  taxYearEndDateSimpleValidationSchema,
  zipCodeValidationSchema,
} from 'src/constants/validation';
import { REQUIRED_VALIDATION_MESSAGE } from 'src/constants/validationMessage';
import { WEBSITE_REG_EXP } from 'src/constants/validationWebsite';

import {
  CommercialVerifierBasicInfoFormFields,
  ConsumerVerificationPreferenceFormFields,
  EmployeeSignupMethodFormFields,
  EmployeeVerificationPreferenceFormFields,
  EmployerBasicInfoFormFields,
  GovernmentVerifierBasicInfoFormFields,
  InitialStepFormFields,
  InitialStepFormValues,
  NoticeToFurnishersFormFields,
  PEOBasicInfoFormFields,
  PEOPayrollProviderBasicInfoFormFields,
  VerifierAccountTypeFormFields,
  VerifierBasicInfoFormFields,
  VerifierBasicInfoFormValues,
} from '../types';

export const initialStepFormZipCodeValidationSchema = yup.object({
  [InitialStepFormFields.homeAddress]: yup.object({
    [AddressFieldsFormFields.street]: yup.string().required(REQUIRED_VALIDATION_MESSAGE),
    [AddressFieldsFormFields.city]: yup.string().required(REQUIRED_VALIDATION_MESSAGE),
    [AddressFieldsFormFields.county]: yup.string().required(REQUIRED_VALIDATION_MESSAGE),
    [AddressFieldsFormFields.state]: yup.string().required(REQUIRED_VALIDATION_MESSAGE),
    [AddressFieldsFormFields.zipCode]: zipCodeValidationSchema.required(
      ERROR_MESSAGE_REQUIRED_ZIP_CODE,
    ),
  }),
});

export const initialStepFormValidationSchema = yup
  .object({
    [InitialStepFormFields.isEmployee]: yup.boolean().required('Required'),
    [InitialStepFormFields.firstName]: yup
      .string()
      .nullable()
      .required(REQUIRED_VALIDATION_MESSAGE),
    [InitialStepFormFields.lastName]: yup.string().nullable().required(REQUIRED_VALIDATION_MESSAGE),
    [InitialStepFormFields.email]: emailValidationSchema.required(REQUIRED_VALIDATION_MESSAGE),
    [InitialStepFormFields.phoneNumber]: phoneNumberValidationSchema.required(
      REQUIRED_VALIDATION_MESSAGE,
    ),
    [InitialStepFormFields.password]: passwordValidationSchema.required(
      REQUIRED_VALIDATION_MESSAGE,
    ),
    [InitialStepFormFields.passwordConfirm]: passwordValidationSchema
      .test((value, { options, createError }) => {
        const formValues: Partial<InitialStepFormValues> = options.context?.formValues;
        const { password } = formValues;

        if (password !== value) {
          return createError({ message: 'Passwords must match' });
        }

        return true;
      })
      .required(REQUIRED_VALIDATION_MESSAGE),
  })
  .when({
    is: ({ isEmployee }: InitialStepFormValues) => isEmployee,
    then: schema => schema.concat(initialStepFormZipCodeValidationSchema),
  });

export const employerBasicInfoFormValidationSchema = yup.object({
  [EmployerBasicInfoFormFields.displayName]: yup
    .string()
    .nullable()
    .required(REQUIRED_VALIDATION_MESSAGE),
  [EmployerBasicInfoFormFields.contactEmail]: emailValidationSchema.required(
    REQUIRED_VALIDATION_MESSAGE,
  ),
  [EmployerBasicInfoFormFields.contactPhoneNumber]: phoneNumberValidationSchema,
  [EmployerBasicInfoFormFields.taxYearEndDate]: taxYearEndDateSimpleValidationSchema,
  [EmployerBasicInfoFormFields.numberOfEmployees]: numberOfEmployeesSimpleValidationSchema,
  [EmployerBasicInfoFormFields.corporateStructure]: yup.string().nullable(),
  [EmployerBasicInfoFormFields.industry]: yup.object({ value: yup.string() }).nullable(),
  [EmployerBasicInfoFormFields.avatarImageFile]: yup.object().nullable(),
  [EmployerBasicInfoFormFields.headquarters]: yup.object({
    [AddressFieldsFormFields.street]: yup.string().required('Required street'),
    [AddressFieldsFormFields.city]: yup.string().required('Required city'),
    [AddressFieldsFormFields.county]: yup.string().nullable(),
    [AddressFieldsFormFields.state]: yup.string().required('Required state'),
    [AddressFieldsFormFields.zipCode]: zipCodeValidationSchema.required(
      ERROR_MESSAGE_REQUIRED_ZIP_CODE,
    ),
  }),
  [EmployerBasicInfoFormFields.disclaimer]: yup.string().nullable(),
});

export const employeeVerificationPreferenceFormValidationSchema = yup.object({
  [EmployeeVerificationPreferenceFormFields.isAutoVerificationRequestEnabled]: yup
    .string()
    .nullable(),
  [EmployeeVerificationPreferenceFormFields.signUpMethod]: yup.string().defined(),
});

export const employeeSignupMethodFormValidationSchema = yup.object({
  [EmployeeSignupMethodFormFields.signUpMethod]: yup.string().defined(),
});

export const employeeAttestationsFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return {
      ...acc,
      [item.name]: yup.boolean().required().isTrue(REQUIRED_VALIDATION_MESSAGE),
    };
  }, {} as yup.ObjectShape);
  return yup.object(validationStep);
};

export const noticeToFurnishersFormValidationSchema = yup.object({
  [NoticeToFurnishersFormFields.isRulesRead]: yup
    .boolean()
    .required()
    .isTrue(REQUIRED_VALIDATION_MESSAGE),
});

export const verifierAccountTypeFormValidation = {
  [VerifierAccountTypeFormFields.accountType]: yup
    .string()
    .nullable()
    .oneOf(Object.values(VerifierType))
    .required(REQUIRED_VALIDATION_MESSAGE),
  [VerifierAccountTypeFormFields.permissiblePurposeReferenceId]: yup
    .string()
    .nullable()
    .required(REQUIRED_VALIDATION_MESSAGE),
};

export const peoAttestationsFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return {
      ...acc,
      [item.name]: yup.boolean().required().isTrue(REQUIRED_VALIDATION_MESSAGE),
    };
  }, {} as yup.ObjectShape);
  return yup.object(validationStep);
};

export const peoPayrollProviderAttestationsFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return {
      ...acc,
      [item.name]: yup.boolean().required().isTrue(REQUIRED_VALIDATION_MESSAGE),
    };
  }, {} as yup.ObjectShape);
  return yup.object(validationStep);
};

export const cvStaffAttestationsFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return {
      ...acc,
      [item.name]: yup.boolean().required().isTrue(REQUIRED_VALIDATION_MESSAGE),
    };
  }, {} as yup.ObjectShape);
  return yup.object(validationStep);
};

export const verifierAttestationsFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return {
      ...acc,
      [item.name]: yup.boolean().required().isTrue(REQUIRED_VALIDATION_MESSAGE),
    };
  }, {} as yup.ObjectShape);
  return yup.object(validationStep);
};

export const verifierAccountTypeFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return {
      ...acc,
      [item.name]: yup.boolean().required().isTrue(REQUIRED_VALIDATION_MESSAGE),
    };
  }, {} as yup.ObjectShape);
  return yup.object({ ...validationStep, ...verifierAccountTypeFormValidation });
};

export const commercialVerifierBasicInfoFormValidationSchema = yup.object({
  [CommercialVerifierBasicInfoFormFields.registeredBusinessName]: yup
    .string()
    .nullable()
    .required(REQUIRED_VALIDATION_MESSAGE),
});

export const governmentVerifierBasicInfoFormValidationSchema = yup.object({
  [GovernmentVerifierBasicInfoFormFields.governmentEntityName]: yup
    .string()
    .nullable()
    .required(REQUIRED_VALIDATION_MESSAGE),
  [GovernmentVerifierBasicInfoFormFields.typeOfGovernment]: yup
    .string()
    .nullable()
    .oneOf(Object.values(VerifierTypeOfGovernment))
    .required(REQUIRED_VALIDATION_MESSAGE),
});

export const verifierBasicInfoFormValidationSchema = yup
  .object({
    [VerifierBasicInfoFormFields.accountType]: yup
      .string()
      .nullable()
      .required(REQUIRED_VALIDATION_MESSAGE),
    [VerifierBasicInfoFormFields.companyDisplayInfo]: yup
      .string()
      .nullable()
      .required(REQUIRED_VALIDATION_MESSAGE),
    [VerifierBasicInfoFormFields.primaryContactEmail]: emailValidationSchema.required(
      REQUIRED_VALIDATION_MESSAGE,
    ),
    [VerifierBasicInfoFormFields.zipCode]: zipCodeValidationSchema,
    [VerifierBasicInfoFormFields.primaryContactPhone]: phoneNumberValidationSchema.required(
      REQUIRED_VALIDATION_MESSAGE,
    ),
    [VerifierBasicInfoFormFields.fein]: yup
      .string()
      .nullable()
      .test('invalid-fein', 'Invalid FEIN', value => !Number.isNaN(Number(value)))
      .length(9, 'Invalid FEIN')
      .required(REQUIRED_VALIDATION_MESSAGE),
    [VerifierBasicInfoFormFields.commercialName]: yup.string().nullable(),
    [VerifierBasicInfoFormFields.industry]: yup
      .object({ value: yup.string() })
      .nullable()
      .required(REQUIRED_VALIDATION_MESSAGE),
    [VerifierBasicInfoFormFields.websiteUrl]: yup
      .string()
      .nullable()
      .matches(WEBSITE_REG_EXP, 'Must be a valid URL')
      .required(REQUIRED_VALIDATION_MESSAGE),
    [VerifierBasicInfoFormFields.avatarImageFile]: yup.object().nullable(),
  })
  .concat(addressFieldsValidationSchema)
  .when({
    is: ({ accountType }: VerifierBasicInfoFormValues) => accountType === 'Commercial',
    then: schema => schema.concat(commercialVerifierBasicInfoFormValidationSchema),
    otherwise: schema => schema.concat(governmentVerifierBasicInfoFormValidationSchema),
  });

export const consumerAttestationsFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return {
      ...acc,
      [item.name]: yup.boolean().required().isTrue(REQUIRED_VALIDATION_MESSAGE),
    };
  }, {} as yup.ObjectShape);
  return yup.object(validationStep);
};

export const peoBasicInfoFormValidationSchema = yup.object({
  [PEOBasicInfoFormFields.displayName]: yup
    .string()
    .nullable()
    .required(REQUIRED_VALIDATION_MESSAGE),
  [PEOBasicInfoFormFields.contactEmail]: emailValidationSchema.required(
    REQUIRED_VALIDATION_MESSAGE,
  ),
  [PEOBasicInfoFormFields.contactPhoneNumber]: phoneNumberValidationSchema,
  [PEOBasicInfoFormFields.taxYearEndDate]: taxYearEndDateSimpleValidationSchema,
  [PEOBasicInfoFormFields.numberOfEmployees]: numberOfEmployeesSimpleValidationSchema,
  [PEOBasicInfoFormFields.fein]: yup
    .string()
    .nullable()
    .test('invalid-fein', 'Invalid FEIN', value => !Number.isNaN(Number(value)))
    .length(9, 'Invalid FEIN')
    .required(REQUIRED_VALIDATION_MESSAGE),
  [PEOBasicInfoFormFields.corporateStructure]: yup.string().nullable(),
  [PEOBasicInfoFormFields.industry]: yup.object({ value: yup.string() }).nullable(),
  [PEOBasicInfoFormFields.avatarImageFile]: yup.object().nullable(),
  [PEOBasicInfoFormFields.headquarters]: yup.object({
    [AddressFieldsFormFields.street]: yup.string().required('Required street'),
    [AddressFieldsFormFields.city]: yup.string().required('Required city'),
    [AddressFieldsFormFields.county]: yup.string().nullable(),
    [AddressFieldsFormFields.state]: yup.string().required('Required state'),
    [AddressFieldsFormFields.zipCode]: zipCodeValidationSchema.required(
      ERROR_MESSAGE_REQUIRED_ZIP_CODE,
    ),
  }),
  [PEOBasicInfoFormFields.disclaimer]: yup.string().nullable(),
});

export const peoPayrollProviderBasicInfoFormValidationSchema = yup.object({
  [PEOPayrollProviderBasicInfoFormFields.displayName]: yup
    .string()
    .nullable()
    .required(REQUIRED_VALIDATION_MESSAGE),
  [PEOPayrollProviderBasicInfoFormFields.contactEmail]: emailValidationSchema.required(
    REQUIRED_VALIDATION_MESSAGE,
  ),
  [PEOPayrollProviderBasicInfoFormFields.contactPhoneNumber]: phoneNumberValidationSchema,
  [PEOPayrollProviderBasicInfoFormFields.taxYearEndDate]: taxYearEndDateSimpleValidationSchema,
  [PEOPayrollProviderBasicInfoFormFields.numberOfEmployees]:
    numberOfEmployeesSimpleValidationSchema,
  [PEOPayrollProviderBasicInfoFormFields.fein]: yup
    .string()
    .nullable()
    .test('invalid-fein', 'Invalid FEIN', value => !Number.isNaN(Number(value)))
    .length(9, 'Invalid FEIN')
    .required(REQUIRED_VALIDATION_MESSAGE),
  [PEOPayrollProviderBasicInfoFormFields.corporateStructure]: yup.string().nullable(),
  [PEOPayrollProviderBasicInfoFormFields.industry]: yup.object({ value: yup.string() }).nullable(),
  [PEOPayrollProviderBasicInfoFormFields.avatarImageFile]: yup.object().nullable(),
  [PEOPayrollProviderBasicInfoFormFields.headquarters]: yup.object({
    [AddressFieldsFormFields.street]: yup.string().required('Required street'),
    [AddressFieldsFormFields.city]: yup.string().required('Required city'),
    [AddressFieldsFormFields.county]: yup.string().nullable(),
    [AddressFieldsFormFields.state]: yup.string().required('Required state'),
    [AddressFieldsFormFields.zipCode]: zipCodeValidationSchema.required(
      ERROR_MESSAGE_REQUIRED_ZIP_CODE,
    ),
  }),
  [PEOPayrollProviderBasicInfoFormFields.disclaimer]: yup.string().nullable(),
});

export const consumerVerificationPreferenceFormValidationSchema = yup.object({
  [ConsumerVerificationPreferenceFormFields.isAutoVerificationRequestEnabled]: yup
    .string()
    .nullable(),
});
